import {
  NetworkError,
  type HttpBody,
  type HttpRequest,
  type HttpResponse,
  type TransportMode,
} from "./types.ts";

export interface CacheContext {
  sourceRevision: string;
  transport: TransportMode | "auto";
  // Change on login/logout, permission or cookie/session changes, and proxy changes.
  sessionKey: string;
}

export interface RequestCacheOptions {
  maxEntries?: number;
  maxBytes?: number;
  maxInFlight?: number;
  now?: () => number;
}

type Entry<T extends HttpBody> = { response: HttpResponse<T>; expiresAt: number; bytes: number };
type Loader<T extends HttpBody> = (
  request: Omit<HttpRequest, "signal">,
) => Promise<HttpResponse<T>>;

// Also catches challenges returned as HTTP 200. Source-specific validation is supplied by the caller.
export function isCacheableResponse<T extends HttpBody>(response: HttpResponse<T>): boolean {
  if (response.status < 200 || response.status >= 300 || response.classification !== "content")
    return false;
  const headers = new Headers(response.headers);
  if (
    /no-store/i.test(headers.get("cache-control") ?? "") ||
    headers.get("cf-mitigated") === "challenge"
  )
    return false;
  const text =
    typeof response.body === "string"
      ? response.body
      : new TextDecoder().decode(response.body.subarray(0, 65536));
return !/id\s*=\s*["']challenge-form["']|<title[^>]*>\s*(?:Just a moment|Attention Required)|cf-chl-/i.test(
    text,
  );
}

export async function createRequestCacheKey(
  request: HttpRequest,
  context: CacheContext,
): Promise<string> {
  const url = new URL(request.url);
  url.hash = "";
  const headers = [...new Headers(request.headers).entries()].sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const serialized = JSON.stringify([
    request.sourceId,
    context.sourceRevision,
    context.transport,
    context.sessionKey,
    request.method,
    url.href,
    headers,
    request.body ?? null,
    request.responseType,
    request.credentials,
    request.timeoutMs ?? null,
    request.cache.scope,
    request.cache.ttlMs,
    request.corsOpen ?? null,
    request.requiresSession ?? null,
    request.purpose ?? "metadata",
  ]);
  // Raw authorization headers and POST bodies are never stored as Map keys.
  if (typeof crypto !== "undefined" && crypto.subtle) {
    try {
      const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(serialized));
      return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    } catch {
      // Fallback below
    }
  }
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < serialized.length; i++) {
    const ch = serialized.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

function cloneResponse<T extends HttpBody>(
  response: HttpResponse<T>,
  fromCache: boolean,
): HttpResponse<T> {
  return {
    ...response,
    headers: { ...response.headers },
    body: structuredClone(response.body),
    fromCache,
  };
}

function withConsumerSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(new NetworkError("ABORTED", "Solicitud cancelada."));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new NetworkError("ABORTED", "Solicitud cancelada."));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
}

/** Memory-only cache. Loaders enforce the transport timeout; consumer aborts do not cancel shared work. */
export class RequestCache<T extends HttpBody> {
  private readonly entries = new Map<string, Entry<T>>();
  private readonly inFlight = new Map<string, Promise<HttpResponse<T>>>();
  private bytes = 0;
  private generation = 0;
  private readonly maxEntries: number;
  private readonly maxBytes: number;
  private readonly maxInFlight: number;
  private readonly now: () => number;

  constructor(options: RequestCacheOptions = {}) {
    this.maxEntries = options.maxEntries ?? 100;
    this.maxBytes = options.maxBytes ?? 8 * 1024 * 1024;
    this.maxInFlight = options.maxInFlight ?? 64;
    this.now = options.now ?? Date.now;
    if (
      ![this.maxEntries, this.maxBytes, this.maxInFlight].every(
        (value) => Number.isSafeInteger(value) && value > 0,
      )
    ) {
      throw new RangeError("Los límites de caché deben ser enteros positivos.");
    }
  }

  async getOrLoad(
    request: HttpRequest,
    context: CacheContext,
    loader: Loader<T>,
    validate: (response: HttpResponse<T>) => boolean = () => true,
  ): Promise<HttpResponse<T>> {
    if (request.signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
    if (!Number.isFinite(request.cache.ttlMs) || request.cache.ttlMs < 0)
      throw new RangeError("TTL inválido.");
    const generation = this.generation;
    const key = await createRequestCacheKey(request, context);
    if (request.signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
    // An invalidation during key generation must not resurrect old session requests.
    if (generation !== this.generation) throw new NetworkError("ABORTED", "La sesión cambió.");
    this.pruneExpired();
    const entry = this.entries.get(key);
    if (entry && validate(cloneResponse(entry.response, true))) {
      this.entries.delete(key);
      this.entries.set(key, entry);
      return cloneResponse(entry.response, true);
    }
    if (entry) this.remove(key);
    let pending = this.inFlight.get(key);
    if (!pending) {
      if (this.inFlight.size >= this.maxInFlight)
        throw new NetworkError("NETWORK_BUSY", "Hay demasiadas solicitudes pendientes.");
      const { signal: _signal, ...sharedRequest } = request;
      pending = Promise.resolve()
        .then(() => loader(sharedRequest))
        .then((response) => {
          if (
            generation === this.generation &&
            request.cache.ttlMs > 0 &&
            isCacheableResponse(response) &&
            validate(cloneResponse(response, false))
          ) {
            this.store(key, response, request.cache.ttlMs);
          }
          return response;
        })
        .finally(() => {
          if (this.inFlight.get(key) === pending) this.inFlight.delete(key);
        });
      this.inFlight.set(key, pending);
    }
    const response = await withConsumerSignal(pending, request.signal);
    if (generation !== this.generation) throw new NetworkError("ABORTED", "La sesión cambió.");
    return cloneResponse(response, false);
  }

  clear(): void {
    this.generation++;
    this.entries.clear();
    this.inFlight.clear();
    this.bytes = 0;
  }

  private remove(key: string): void {
    const entry = this.entries.get(key);
    if (entry) this.bytes -= entry.bytes;
    this.entries.delete(key);
  }

  private pruneExpired(): void {
    const now = this.now();
    for (const [key, entry] of this.entries) if (entry.expiresAt <= now) this.remove(key);
  }

  private store(key: string, response: HttpResponse<T>, ttlMs: number): void {
    const bytes =
      (typeof response.body === "string"
        ? new TextEncoder().encode(response.body).byteLength
        : response.body.byteLength) +
      new TextEncoder().encode(JSON.stringify(response.headers) + response.finalUrl).byteLength;
    if (bytes > this.maxBytes) return;
    this.remove(key);
    while (this.entries.size >= this.maxEntries || this.bytes + bytes > this.maxBytes) {
      this.remove(this.entries.keys().next().value!);
    }
    this.entries.set(key, {
      response: cloneResponse(response, false),
      expiresAt: this.now() + ttlMs,
      bytes,
    });
    this.bytes += bytes;
  }
}
